import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import { useLocation, Link as RouterLink } from "react-router-dom";
import clsx from "clsx";
import {
  makeStyles,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  Button,
  MenuItem,
  IconButton,
  Menu,
  useTheme,
  useMediaQuery,
  Avatar,
  Badge,
  withStyles,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from "@material-ui/core";
import { NotificationsIcon, CachedIcon } from "../components/SvgIcons";
import api from "../services/api";
import AppBarNavigationMenu from "../components/AppBarNavigationMenu";
import { LayoutProvider } from "../context/Layout/LayoutContext";

import PlanPermissionsContext from "../context/PlanPermissions/PlanPermissionsContext";

import NotificationsPopOver from "../components/NotificationsPopOver";
import BirthdayModal from "../components/BirthdayModal";
import UserModal from "../components/UserModal";
import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import toastError from "../errors/toastError";
import AnnouncementsPopover from "../components/AnnouncementsPopover";
import ChatPopover from "../pages/Chat/ChatPopover";
import { useDate } from "../hooks/useDate";
import ColorModeContext from "../layout/themeContext";
import { Brightness4Icon, Brightness7Icon } from "../components/SvgIcons";
import { getBackendUrl } from "../config";
import useSettings from "../hooks/useSettings";
import useSocketListener from "../hooks/useSocketListener";
import { VolumeUpIcon, VolumeOffIcon, GlobeIcon } from "../components/SvgIcons";
import Fab from "@material-ui/core/Fab";
import { AddIcon, HomeIcon, ChatIcon, PersonIcon, SettingsIcon, GetAppIcon, AccountCircleIcon } from "../components/SvgIcons";
import BottomNavigation from "@material-ui/core/BottomNavigation";
import BottomNavigationAction from "@material-ui/core/BottomNavigationAction";
import usePWAInstall from "../hooks/usePWAInstall";
import IOSInstallInstructionsDialog from "../components/IOSInstallInstructionsDialog";
import toastUtils from "../utils/toastUtils";

const backendUrl = getBackendUrl();
const bottomNavHeight = 64;


const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100vh",
    position: "relative",
    // Mobile-first responsive heights
    [theme.breakpoints.down("xs")]: {
      height: "100vh",
      flexDirection: "column",
    },
    [theme.breakpoints.down("sm")]: {
      height: "calc(100vh - 56px)",
      paddingBottom: `${bottomNavHeight}px`,
    },
    backgroundColor: theme.palette.fancyBackground,
    "& .MuiButton-outlinedPrimary": {
      color: theme.palette.primary.main, // Usa cor do tema
      border: `1px solid ${theme.palette.primary.main}40`,
      borderRadius: "8px",
      fontWeight: 600,
      textTransform: "none",
      transition: "all 0.3s ease",
      "&:hover": {
        backgroundColor: `${theme.palette.primary.main}10`,
        borderColor: theme.palette.primary.main,
        transform: "translateY(-1px)",
        boxShadow: `0 4px 12px ${theme.palette.primary.main}30`,
      },
    },
    "& .MuiTab-textColorPrimary.Mui-selected": {
      color: theme.palette.primary.main, // Usa cor do tema
      fontWeight: 700,
    },
  },

  chip: {
    background: "red",
    color: "white",
  },

  avatar: {
    width: "100%",
  },

  toolbar: {
    paddingRight: 24,
    color: theme.palette.dark.main,
    // Usa a cor primária do tema para o fundo do AppBar
    background: theme.palette.primary.main, // Mudança principal aqui
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)", // Sombra sutil
    transition: "all 0.3s ease",
  },

  // 🎂 NOVO: Toolbar com animação de aniversário
  birthdayToolbar: {
    paddingRight: 24,
    color: theme.palette.dark.main,
    background: "linear-gradient(45deg, #ff6b6b, #feca57, #48dbfb, #ff6b6b)",
    backgroundSize: "400% 400%",
    animation: "$gradientShift 3s ease infinite",
    boxShadow: "0 2px 20px rgba(255, 107, 107, 0.5)",
    position: "relative",
    overflow: "hidden",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M20 20c0-5.5-4.5-10-10-10s-10 4.5-10 10 4.5 10 10 10 10-4.5 10-10zm10 0c0-5.5-4.5-10-10-10s-10 4.5-10 10 4.5 10 10 10 10-4.5 10-10z'/%3E%3C/g%3E%3C/svg%3E")`,
      opacity: 0.3,
      animation: "$float 4s ease-in-out infinite",
    },
    "&::after": {
      content: '"🎉🎂🎈🎊"',
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      fontSize: "2rem",
      opacity: 0.1,
      animation: "$rotate 10s linear infinite",
      pointerEvents: "none",
    }
  },

    // 🎂 NOVO: Título com animação de aniversário
  birthdayTitle: {
    flexGrow: 1,
    fontSize: 14,
    color: "white",
    fontWeight: 700,
    letterSpacing: "0.025em",
    position: "relative",
    zIndex: 2,
    textShadow: "0 0 10px rgba(255, 255, 255, 0.5), 0 0 20px rgba(255, 255, 255, 0.3)",
    animation: "$titleGlow 2s ease-in-out infinite alternate",
    "&::before": {
      content: '"🎉 "',
      animation: "$bounce 1s infinite",
      display: "inline-block",
    },
    "&::after": {
      content: '" 🎂"',
      animation: "$bounce 1.5s infinite",
      display: "inline-block",
    }
  },

  // 🎂 NOVO: Confetes caindo
  confetti: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: "none",
    overflow: "hidden",
    zIndex: 1,
    "&::before, &::after": {
      content: '"🎊🎉🎈🎁✨🌟💫⭐"',
      position: "absolute",
      top: "-10px",
      left: 0,
      right: 0,
      fontSize: "1.2rem",
      letterSpacing: "2rem",
      animation: "$confettiFall 8s linear infinite",
      opacity: 0.6,
    },
    "&::after": {
      animationDelay: "4s",
      fontSize: "0.8rem",
      letterSpacing: "1.5rem",
    }
  },

  // 🎂 NOVO: Badge de aniversário animado
  birthdayBadge: {
    position: "absolute",
    top: -5,
    right: -5,
    backgroundColor: "#ff4444",
    color: "white",
    borderRadius: "50%",
    width: 24,
    height: 24,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "12px",
    fontWeight: "bold",
    animation: "$heartbeat 1.5s infinite",
    zIndex: 3,
    border: "2px solid white",
    boxShadow: "0 0 10px rgba(255, 68, 68, 0.5)",
  },

  // 🎂 NOVO: Container do avatar de aniversário
  birthdayAvatarContainer: {
    position: "relative",
    "&::before": {
      content: '""',
      position: "absolute",
      top: -8,
      left: -8,
      right: -8,
      bottom: -8,
      borderRadius: "50%",
      background: "linear-gradient(45deg, #ff6b6b, #feca57, #48dbfb, #ff6b6b)",
      backgroundSize: "400% 400%",
      animation: "$gradientShift 2s ease infinite",
      zIndex: 0,
    },
    "&::after": {
      content: '"🎂"',
      position: "absolute",
      top: -15,
      right: -10,
      fontSize: "20px",
      animation: "$floatEmoji 3s ease-in-out infinite",
      zIndex: 4,
    }
  },

  // 🎂 NOVO: Efeitos de brilho/sparkle
  sparkles: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: "none",
    zIndex: 1,
    "&::before": {
      content: '"✨"',
      position: "absolute",
      top: "20%",
      left: "10%",
      fontSize: "1rem",
      animation: "$sparkle 2s ease-in-out infinite",
      animationDelay: "0s",
    },
    "&::after": {
      content: '"⭐"',
      position: "absolute",
      top: "60%",
      right: "15%",
      fontSize: "0.8rem",
      animation: "$sparkle 2s ease-in-out infinite",
      animationDelay: "1s",
    }
  },

  toolbarIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end", // Alinha o botão de fechar à direita
    padding: "0 8px",
    minHeight: "48px", // Altura consistente com o layout antigo
    [theme.breakpoints.up('sm')]: {
      minHeight: "48px", // Mantém consistente
    },
    backgroundColor: "#ffffff", // Fundo branco fixo
    borderBottom: `1px solid ${theme.palette.divider}`, // Linha sutil para separação
    transition: "all 0.3s ease",
  },

  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },


  title: {
    flexGrow: 1,
    fontSize: 14,
    color: "white",
    fontWeight: 600,
    letterSpacing: "0.025em",
  },


  appBarSpacer: {
    minHeight: "56px", // Altura padrão da AppBar no mobile
    [theme.breakpoints.up('sm')]: {
      minHeight: "64px", // Altura da AppBar no desktop
    },
  },

  content: {
    flex: 1,
    overflow: "auto",
    padding: 0,
    margin: 0,
    width: "100%",
    // Mobile content adjustments
    [theme.breakpoints.down('sm')]: {
      paddingBottom: `${bottomNavHeight + 8}px`,
      minHeight: `calc(100vh - 56px - ${bottomNavHeight}px)`,
    },
    // Desktop adjustments - full width without drawer margin
    [theme.breakpoints.up('md')]: {
      minHeight: `calc(100vh - 48px)`, // Apenas altura da AppBar
    },
    // Smooth scrolling
    scrollBehavior: 'smooth',
    '-webkit-overflow-scrolling': 'touch',
  },


  container: {
    padding: 0,
    margin: 0,
    maxWidth: "none",
    width: "100%",
  },

  containerWithScroll: {
    flex: 1,
    overflowY: "scroll",
    overflowX: "hidden",
    ...theme.scrollbarStyles,
    borderRadius: "8px",
    border: "2px solid transparent",
    "&::-webkit-scrollbar": {
      display: "none",
    },
    "-ms-overflow-style": "none",
    "scrollbar-width": "none",
  },

  NotificationsPopOver: {
    // Mantém original
  },

  logo: {
    width: "100%",
    height: "45px",
    maxWidth: 180,
    [theme.breakpoints.down("sm")]: {
      width: "auto",
      height: "100%",
      maxWidth: 180,
    },
    logo: theme.logo,
    content:
      "url(" +
      (theme.mode === "light"
        ? theme.calculatedLogoLight()
        : theme.calculatedLogoDark()) +
      ")",
    transition: "all 0.3s ease", // Transição suave
    "&:hover": {
      transform: "scale(1.02)", // Pequeno zoom no hover
    },
  },

  hideLogo: {
    display: "none",
  },

  avatar2: {
    width: theme.spacing(4),
    height: theme.spacing(4),
    cursor: "pointer",
    borderRadius: "50%",
    border: "2px solid #ccc",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "scale(1.05)",
      borderColor: theme.palette.primary.main, // Usa cor do tema
    },
  },

  updateDiv: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },

  // Botões da toolbar melhorados
  toolbarButton: {
    color: "rgba(255, 255, 255, 0.9)",
    borderRadius: "8px",
    padding: "8px",
    margin: "0 2px",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)",
      transform: "translateY(-1px)",
    },
    "&:active": {
      transform: "translateY(0)",
    },
  },


  // Seletor de idioma melhorado
  languageSelector: {
    position: "relative",
    display: "inline-block",
    "& > button": {
      background: "rgba(255, 255, 255, 0.1)",
      border: "none",
      borderRadius: "8px",
      color: "rgba(255, 255, 255, 0.9)",
      fontSize: "18px",
      padding: "8px 12px",
      cursor: "pointer",
      transition: "all 0.3s ease",
      "&:hover": {
        background: "rgba(255, 255, 255, 0.2)",
        transform: "translateY(-1px)",
      },
    },
    "& > div": {
      position: "absolute",
      top: "45px",
      left: "0",
      background: "#fff",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
      borderRadius: "8px",
      padding: "8px",
      zIndex: 1000,
      minWidth: "120px",
      "& button": {
        background: "none",
        border: "none",
        color: "#374151",
        display: "block",
        width: "100%",
        padding: "8px 12px",
        textAlign: "left",
        borderRadius: "6px",
        fontSize: "14px",
        fontWeight: 500,
        transition: "all 0.2s ease",
        "&:hover": {
          background: `${theme.palette.primary.main}10`, // Usa cor do tema
          color: theme.palette.primary.main, // Usa cor do tema
          transform: "none",
        },
      },
    },
  },

  // Mobile bottom navigation
  mobileBottomNav: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    height: `${bottomNavHeight}px`,
    backgroundColor: theme.palette.background.paper,
    borderTop: `1px solid ${theme.mode === "light" ? "#e0e0e0" : "#424242"}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-around',
    zIndex: theme.zIndex.appBar,
    boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.1)',
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },

  // Bottom nav items
  bottomNavItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px 12px',
    borderRadius: '8px',
    minWidth: '64px',
    transition: 'all 0.2s ease',
    textDecoration: 'none',
    color: theme.mode === "light" ? "#666" : "#aaa",
    fontSize: '0.75rem',
    fontWeight: 500,
    '&.active': {
      color: theme.palette.primary.main,
      backgroundColor: `${theme.palette.primary.main}10`,
    },
    '&:hover': {
      backgroundColor: theme.mode === "light"
        ? "rgba(0, 0, 0, 0.04)"
        : "rgba(255, 255, 255, 0.04)",
    },
    '& .MuiSvgIcon-root': {
      fontSize: '1.5rem',
      marginBottom: '4px',
    },
  },

  // Floating Action Button for mobile
  mobileFab: {
    position: 'fixed',
    bottom: `${bottomNavHeight + 16}px`,
    right: '16px',
    zIndex: theme.zIndex.speedDial,
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },

    // 🎂 NOVO: Avatar com animação de aniversário
  birthdayAvatar: {
    width: theme.spacing(4),
    height: theme.spacing(4),
    cursor: "pointer",
    borderRadius: "50%",
    background: "linear-gradient(45deg, #ff6b6b, #feca57)",
    border: "3px solid #fff",
    position: "relative",
    animation: "$birthdayPulse 2s infinite",
    boxShadow: "0 0 20px rgba(255, 107, 107, 0.5)",
    "&::before": {
      content: '"🎉"',
      position: "absolute",
      top: -5,
      right: -5,
      fontSize: 12,
      animation: "$bounce 1s infinite",
    },
    "&:hover": {
      transform: "scale(1.05)",
    },
  },

  // Badge animado
  animatedBadge: {
    "& .MuiBadge-badge": {
      animation: "$heartbeat 2s infinite",
    },
  },

  // 🎂 NOVAS ANIMAÇÕES FESTIVAS
  "@keyframes gradientShift": {
    "0%": { backgroundPosition: "0% 50%" },
    "50%": { backgroundPosition: "100% 50%" },
    "100%": { backgroundPosition: "0% 50%" }
  },

  "@keyframes titleGlow": {
    "0%": { 
      textShadow: "0 0 10px rgba(255, 255, 255, 0.5), 0 0 20px rgba(255, 255, 255, 0.3)",
      transform: "scale(1)"
    },
    "100%": { 
      textShadow: "0 0 20px rgba(255, 255, 255, 0.8), 0 0 30px rgba(255, 255, 255, 0.5)",
      transform: "scale(1.02)"
    }
  },

  "@keyframes confettiFall": {
    "0%": { 
      transform: "translateY(-100px) rotate(0deg)",
      opacity: 1
    },
    "100%": { 
      transform: "translateY(100px) rotate(360deg)",
      opacity: 0
    }
  },

  "@keyframes float": {
    "0%, 100%": { transform: "translateY(0px)" },
    "50%": { transform: "translateY(-10px)" }
  },

  "@keyframes rotate": {
    "0%": { transform: "translate(-50%, -50%) rotate(0deg)" },
    "100%": { transform: "translate(-50%, -50%) rotate(360deg)" }
  },

  "@keyframes floatEmoji": {
    "0%, 100%": { 
      transform: "translateY(0px) rotate(-5deg)",
      opacity: 1
    },
    "50%": { 
      transform: "translateY(-8px) rotate(5deg)",
      opacity: 0.8
    }
  },

  "@keyframes sparkle": {
    "0%, 100%": { 
      transform: "scale(0) rotate(0deg)",
      opacity: 0
    },
    "50%": { 
      transform: "scale(1) rotate(180deg)",
      opacity: 1
    }
  },

  "@keyframes birthdayPulse": {
    "0%, 100%": { 
      transform: "scale(1)",
      boxShadow: "0 0 20px rgba(255, 107, 107, 0.5)"
    },
    "50%": { 
      transform: "scale(1.1)",
      boxShadow: "0 0 30px rgba(255, 107, 107, 0.8)"
    }
  },

  "@keyframes bounce": {
    "0%, 20%, 50%, 80%, 100%": { transform: "translateY(0)" },
    "40%": { transform: "translateY(-5px)" },
    "60%": { transform: "translateY(-3px)" }
  },

  "@keyframes heartbeat": {
    "0%": { transform: "scale(1)" },
    "14%": { transform: "scale(1.1)" },
    "28%": { transform: "scale(1)" },
    "42%": { transform: "scale(1.1)" },
    "70%": { transform: "scale(1)" },
  },
}));

const StyledBadge = withStyles((theme) => ({
  badge: {
    backgroundColor: "#44b700",
    color: "#44b700",
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    "&::after": {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      animation: "$ripple 1.2s infinite ease-in-out",
      border: "1px solid currentColor",
      content: '""',
    },
  },

  "@keyframes ripple": {
    "0%": {
      transform: "scale(.8)",
      opacity: 1,
    },
    "100%": {
      transform: "scale(2.4)",
      opacity: 0,
    },
  },
}))(Badge);

const SmallAvatar = withStyles((theme) => ({
  root: {
    width: 22,
    height: 22,
    border: `2px solid ${theme.palette.background.paper}`,
  },
}))(Avatar);

// Mobile Bottom Navigation Component
const MobileBottomNavigation = ({ user, onProfileClick }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(
    theme?.breakpoints?.down && typeof theme.breakpoints.down === 'function'
      ? theme.breakpoints.down('sm')
      : '(max-width: 599px)'
  );
  const location = useLocation();
  const [value, setValue] = useState(0);

  const bottomNavItems = React.useMemo(() => {
    if (!user || typeof user !== 'object') return [];

    try {
      const items = [
        {
          label: i18n.t("mainDrawer.listItems.tickets"),
          icon: <ChatIcon />,
          path: "/tickets",
          show: true
        },
        {
          label: i18n.t("mainDrawer.listItems.contacts"),
          icon: <PersonIcon />,
          path: "/contacts",
          show: user?.showContacts === "enabled"
        },
        {
          label: i18n.t("mainDrawer.listItems.settings"),
          icon: <SettingsIcon />,
          path: "/settings",
          show: user?.profile === "admin"
        },
        {
          label: i18n.t("mainDrawer.appBar.user.profile"),
          icon: <AccountCircleIcon />,
          path: null, // Sem rota - abre menu
          isProfileButton: true,
          show: true
        },
      ];

      return items.filter(item => item && item.show);
    } catch (error) {
      console.warn('Error creating bottom nav items:', error);
      return [];
    }
  }, [user]);

  React.useEffect(() => {
    if (!isMobile || !location || !location.pathname || !Array.isArray(bottomNavItems)) return;

    try {
      const activeIndex = bottomNavItems.findIndex(item =>
        item && item.path && (
          location.pathname === item.path ||
          (item.path !== "/" && location.pathname.startsWith(item.path))
        )
      );
      if (activeIndex !== -1) {
        setValue(activeIndex);
      }
    } catch (error) {
      console.warn('Error finding active navigation item:', error);
    }
  }, [location?.pathname, isMobile, bottomNavItems]);

  if (!isMobile || !user || bottomNavItems.length === 0) return null;

  return (
    <BottomNavigation
      value={value}
      onChange={(event, newValue) => setValue(newValue)}
      className={classes.mobileBottomNav}
      showLabels
    >
      {Array.isArray(bottomNavItems) && bottomNavItems.map((item, index) => {
        if (!item) return null;

        // Botão de perfil - abre menu em vez de navegar
        if (item.isProfileButton) {
          return (
            <BottomNavigationAction
              key="profile"
              label={item.label}
              icon={item.icon}
              onClick={(e) => {
                e.preventDefault();
                if (onProfileClick) {
                  onProfileClick(e);
                }
              }}
              className={classes.bottomNavItem}
            />
          );
        }

        // Itens normais com navegação
        if (!item.path) return null;
        return (
          <BottomNavigationAction
            key={item.path}
            label={item.label}
            icon={item.icon}
            component={RouterLink}
            to={item.path}
            className={classes.bottomNavItem}
          />
        );
      })}
    </BottomNavigation>
  );
};

// Mobile Floating Action Button
const MobileFAB = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(
    theme?.breakpoints?.down && typeof theme.breakpoints.down === 'function'
      ? theme.breakpoints.down('sm')
      : '(max-width: 599px)'
  );

  if (!isMobile) return null;

  return (
    <Fab
      color="primary"
      aria-label="add"
      className={classes.mobileFab}
      component={RouterLink}
      to="/tickets"
    >
      <AddIcon />
    </Fab>
  );
};

const LoggedInLayout = ({ children, themeToggle }) => {
  const classes = useStyles();
  const theme = useTheme();
  const colorModeContext = useContext(ColorModeContext);

  const { colorMode } = colorModeContext || {};
  const authContext = useContext(AuthContext);
  const { handleLogout, loading, user, setUser, socket, isAuth } = authContext || {};
  const { permissions } = useContext(PlanPermissionsContext);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const isMobile = useMediaQuery(
    theme?.breakpoints?.down && typeof theme.breakpoints.down === 'function'
      ? theme.breakpoints.down('sm')
      : '(max-width: 599px)'
  );

  const [showAnnouncementsModal, setShowAnnouncementsModal] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);

  // 🎂 NOVOS STATES PARA ANIVERSÁRIO
  const [birthdayModalOpen, setBirthdayModalOpen] = useState(false);
  const [userHasBirthday, setUserHasBirthday] = useState(false);
  const [birthdayData, setBirthdayData] = useState({ users: [], contacts: [], settings: null });

  // 📱 PWA Install States
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const { isInstallable, isInstalled, isIOS, isAndroid, installPWA } = usePWAInstall();

  const greaterThenSm = useMediaQuery(
    theme?.breakpoints?.up && typeof theme.breakpoints.up === 'function'
      ? theme.breakpoints.up("sm")
      : '(min-width: 600px)'
  );

  const [volume, setVolume] = useState(() => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        return localStorage.getItem("volume") || 1;
      }
      return 1;
    } catch (error) {
      console.log('LocalStorage não disponível:', error);
      return 1;
    }
  });

  const { dateToClient } = useDate();
  const [profileUrl, setProfileUrl] = useState(null);
  const [updateInProgress, setUpdateInProgress] = useState(false);

  // Language settings - moved before early return
  const [enabledLanguages, setEnabledLanguages] = useState(["pt-BR", "en", "es"]);
  const settingsHook = useSettings();
  const { getAll } = settingsHook || {};

  // 🎂 FUNÇÃO PARA VERIFICAR SE JÁ MOSTROU O MODAL HOJE
  const hasShownBirthdayModalToday = () => {
    try {
      if (typeof window === 'undefined' || !window.localStorage) {
        return false;
      }
      const today = new Date().toDateString();
      const lastShown = localStorage.getItem(`lastBirthdayModalShown_${user?.id}`);
      return lastShown === today;
    } catch (error) {
      console.log('Erro ao verificar modal de aniversário:', error);
      return false;
    }
  };

  // 🎂 FUNÇÃO PARA MARCAR QUE O MODAL FOI MOSTRADO HOJE
  const markBirthdayModalShown = () => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const today = new Date().toDateString();
        localStorage.setItem(`lastBirthdayModalShown_${user?.id}`, today);
      }
    } catch (error) {
      console.log('localStorage não disponível:', error);
    }
  };

  const playNotesSequence = (audioContext) => {
    try {
      // Sequência de notas "Happy Birthday" simplificada
      const notes = [
        { freq: 261.63, duration: 0.3 }, // C
        { freq: 293.66, duration: 0.3 }, // D
        { freq: 329.63, duration: 0.6 }, // E
        { freq: 261.63, duration: 0.3 }, // C
        { freq: 349.23, duration: 0.6 }, // F
        { freq: 329.63, duration: 1.0 }  // E
      ];

      let currentTime = audioContext.currentTime;

      notes.forEach((note, index) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.frequency.setValueAtTime(note.freq, currentTime);
        oscillator.type = 'triangle';

        gainNode.gain.setValueAtTime(0.1, currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, currentTime + note.duration);

        oscillator.start(currentTime);
        oscillator.stop(currentTime + note.duration);

        currentTime += note.duration + 0.1;
      });

    } catch (error) {
      console.log('Erro ao reproduzir sequência de notas:', error);
    }
  };

  // 🎂 FUNÇÃO PARA TOCAR SOM FESTIVO (OPCIONAL)
  const playBirthdaySound = () => {
    try {
      // Verificar se estamos no ambiente browser
      if (typeof window === 'undefined') {
        return;
      }

      // Verificar se Web Audio API está disponível
      if (!window.AudioContext && !window.webkitAudioContext) {
        console.log('Web Audio API não disponível');
        return;
      }

      // Verificar se o usuário interagiu com a página (necessário para iOS)
      if (typeof document !== 'undefined' && document.hidden) {
        console.log('Página não está visível, som não será reproduzido');
        return;
      }

      // Criar sequência de tons festivos usando Web Audio API
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const audioContext = new AudioCtx();

      // Verificar se o contexto está suspenso (comum em mobile)
      if (audioContext.state === 'suspended') {
        audioContext.resume().then(() => {
          playNotesSequence(audioContext);
        }).catch((error) => {
          console.log('Não foi possível resumir o contexto de áudio:', error);
        });
      } else {
        playNotesSequence(audioContext);
      }

    } catch (error) {
      console.log('Som de aniversário não disponível:', error);
    }
  };

  // 🎂 FUNÇÃO PARA VERIFICAR ANIVERSÁRIOS
  const checkTodayBirthdays = async () => {
    try {
      const { data } = await api.get("/birthdays/today");
      const birthdayInfo = data.data;

      setBirthdayData(birthdayInfo);

      // Verificar se o usuário logado faz aniversário hoje
      const userBirthday = birthdayInfo.users.find(u => u.id === user.id);
      setUserHasBirthday(!!userBirthday);

      // Abrir modal se há aniversariantes E se ainda não foi mostrado hoje para este usuário
      const hasBirthdays = birthdayInfo.users.length > 0 || birthdayInfo.contacts.length > 0;
      if (hasBirthdays && !hasShownBirthdayModalToday()) {
        // Delay para dar tempo do layout carregar
        setTimeout(() => {
          setBirthdayModalOpen(true);
          markBirthdayModalShown(); // Marcar que foi mostrado

          // 🎂 TOCAR SOM FESTIVO SE HOUVER ANIVERSARIANTES
          if (birthdayInfo.users.some(u => u.id === user.id)) {
            playBirthdaySound();
          }
        }, 1500);
      }
    } catch (error) {
      // Verificar se é um erro de permissão (recurso não disponível)
      if (error?.response?.status === 403) {
        console.warn('Acesso negado à verificação de aniversários - recurso pode não estar disponível no plano atual');
        // Não mostrar toast para erros 403, pois são esperados quando recursos não estão ativos
      } else if (error?.response?.status === 500) {
        console.warn('Erro interno do servidor na verificação de aniversários');
        // Não mostrar toast para erros 500
      } else {
        console.error("Error checking birthdays:", error);
      }
    }
  };

  // All useEffect hooks moved before early return
  useEffect(() => {
    async function fetchSettings() {
      try {
        if (typeof getAll !== 'function') {
          console.warn('getAll is not a function:', getAll);
          return;
        }
        const settings = await getAll();
        if (!Array.isArray(settings)) {
          console.warn('Settings response is not an array:', settings);
          return;
        }
        const enabledLanguagesSetting = settings.find(
          (s) => s && s.key === "enabledLanguages"
        )?.value;
        let langs = ["pt-BR", "en", "es"];
        try {
          if (enabledLanguagesSetting && typeof enabledLanguagesSetting === 'string') {
            const parsedLangs = JSON.parse(enabledLanguagesSetting);
            if (Array.isArray(parsedLangs)) {
              langs = parsedLangs;
            }
          }
        } catch (error) {
          console.warn('Error parsing enabled languages:', error);
        }
        console.log(
          "Layout - enabledLanguages carregadas:",
          langs,
          "para companyId:",
          user?.companyId
        );
        setEnabledLanguages(langs);
      } catch (error) {
        console.log("Layout - erro ao carregar enabledLanguages:", error);
        setEnabledLanguages(["pt-BR", "en", "es"]); // fallback
      }
    }
    if (user?.companyId) {
      fetchSettings();
    }
  }, [user?.companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  // 🎂 EFEITO PARA VERIFICAR ANIVERSÁRIOS AO FAZER LOGIN
  useEffect(() => {
    if (user?.id) {
      checkTodayBirthdays();
    }
  }, [user]);

  // 🎂 LISTENER PARA EVENTOS DE ANIVERSÁRIO VIA SOCKET
  useEffect(() => {
    if (user?.companyId && socket && typeof socket.on === 'function') {
      const companyId = user.companyId;
  
      const onUserBirthday = (data) => {
        console.log("User birthday event:", data);
        checkTodayBirthdays();
        if (data.user.id === user.id) {
          playBirthdaySound();
        }
      };
  
      const onContactBirthday = (data) => {
        console.log("Contact birthday event:", data);
        checkTodayBirthdays();
      };
  
      try {
        socket.on('user-birthday', onUserBirthday);
        socket.on('contact-birthday', onContactBirthday);
  
        return () => {
          if (socket && typeof socket.off === 'function') {
            socket.off('user-birthday', onUserBirthday);
            socket.off('contact-birthday', onContactBirthday);
          }
        };
      } catch (error) {
        console.error('Erro ao adicionar listeners de aniversário:', error);
      }
    }
  }, [user, socket]);



  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        const { data } = await api.get("/announcements/for-company", {
          params: {
            status: true,
            pageNumber: "1"
          }
        });

        // Filtra apenas os informativos ativos e não expirados
        const activeAnnouncements = data.records.filter(announcement => {
          const isActive = announcement.status === true || announcement.status === "true";
          const isNotExpired = !announcement.expiresAt || new Date(announcement.expiresAt) > new Date();
          return isActive && isNotExpired;
        });

        setAnnouncements(activeAnnouncements);

        // Mostra o modal apenas se houver informativos ativos
        if (activeAnnouncements.length > 0) {
          setShowAnnouncementsModal(true);
        }
      } catch (err) {
        toastError(err);
      }
    };

    if (user?.id) {
      fetchAnnouncements();
    }
  }, [user?.id]);

  useEffect(() => {
    // if (localStorage.getItem("public-token") === null) {
    //   handleLogout()
    // }

    // Verificar se user está completamente carregado
    if (!user || !user.id) return;

    if (user.defaultTheme === "dark" && theme.mode === "light") {
      colorMode.toggleColorMode();
    }
  }, [user, theme, colorMode]);


  useEffect(() => {
    const companyId = user?.companyId;

    if (companyId) {
      const buildProfileUrl = () => {
        let savedProfileImage = null;
        try {
          if (typeof window !== 'undefined' && window.localStorage) {
            savedProfileImage = localStorage.getItem("profileImage");
          }
        } catch (error) {
          console.log('Erro ao acessar localStorage para profileImage:', error);
        }
        
        const currentProfileImage = savedProfileImage || user.profileImage;

        if (currentProfileImage) {
          return `${backendUrl}/public/company${companyId}/user/${currentProfileImage}`;
        }
        return `${backendUrl}/public/app/noimage.png`;
      };

      setProfileUrl(buildProfileUrl());
    }
  }, [user?.companyId, user?.profileImage, backendUrl]);

  // Callbacks dos eventos
  const handleAuthEvent = useCallback((data) => {
    if (data.user.id === +user?.id) {
      toastError("Sua conta foi acessada em outro computador.");
      setTimeout(() => {
        localStorage.clear();
        window.location.reload();
      }, 1000);
    }
  }, [user?.id]);

  const handleUserUpdate = useCallback((data) => {
    if (data.action === "update" && data.user.id === +user?.id) {
      if (data.user.profileImage) {
        const newProfileUrl = `${backendUrl}/public/company${user?.companyId}/user/${data.user.profileImage}`;
        setProfileUrl(newProfileUrl);
        try {
          if (typeof window !== 'undefined' && window.localStorage) {
            localStorage.setItem("profileImage", data.user.profileImage);
          }
        } catch (error) {
          console.log('Erro ao salvar profileImage:', error);
        }
      }
    }
  }, [user?.companyId, user?.id, backendUrl]);

  // Registrar listeners
  useSocketListener(socket, user, 'auth', handleAuthEvent);
  useSocketListener(socket, user, 'user', handleUserUpdate);

  // Status do usuário
  useEffect(() => {
    if (socket?.emit && user?.companyId) {
      socket.emit("userStatus");

      const interval = setInterval(() => {
        socket?.emit && socket.emit("userStatus");
      }, 1000 * 60 * 5);

      return () => clearInterval(interval);
    }
  }, [socket, user?.companyId]);

  // Early return if contexts are not loaded yet - moved after all hooks
  if (!theme || loading === true || !theme?.breakpoints || !authContext || !colorModeContext) {
    console.log('Layout loading - theme:', !!theme, 'loading:', loading, 'breakpoints:', !!theme?.breakpoints, 'authContext:', !!authContext, 'colorModeContext:', !!colorModeContext);
    return <BackdropLoading />;
  }

  // Additional safety check for user properties - mas só se estiver autenticado
  if (loading === false && isAuth && (!user || typeof user !== 'object' || !user.id || !user.name)) {
    console.warn('User object is incomplete or invalid for authenticated user:', user);
    return <BackdropLoading />;
  }

  // Se não está autenticado, renderizar children sem layout (para login/signup)
  if (loading === false && !isAuth) {
    console.log('User not authenticated, rendering children without layout');
    return children;
  }

  const handleUpdateStart = () => {
    setUpdateInProgress(true);
  };

  const handleUpdateComplete = () => {
    setUpdateInProgress(false);
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    handleCloseMenu();
  };

  const handleClickLogout = () => {
    handleCloseMenu();
    handleLogout();
  };

  const handleLanguageMenuClick = (lng) => {
    handleLanguageChange(lng);
    handleCloseMenu();
  };

  // Handler para alternar volume no menu do avatar
  const handleVolumeToggle = () => {
    setVolume(!volume);
    handleCloseMenu();
  };

  // 📱 PWA Install Handlers
  const handlePWAInstall = async () => {
    handleCloseMenu();

    if (isInstalled) {
      toastUtils.info(i18n.t('pwa.alreadyInstalled'), 'PWA');
      return;
    }

    if (isIOS) {
      setShowIOSInstructions(true);
      return;
    }

    const result = await installPWA();

    if (result.success) {
      toastUtils.success(i18n.t('pwa.installSuccess'), 'PWA');
    } else if (result.reason === 'user_dismissed') {
      toastUtils.info(i18n.t('pwa.installCanceled'), 'PWA');
    } else if (result.reason === 'no_prompt') {
      toastUtils.warning(i18n.t('pwa.notSupported'), 'PWA');
    } else {
      toastUtils.error(i18n.t('pwa.installError'), 'PWA');
    }
  };

  const handleShowIOSInstructions = () => {
    handleCloseMenu();
    setShowIOSInstructions(true);
  };

  const handleRefreshPage = () => {
    window.location.reload(false);
  };

  const handleLanguageChange = (lng) => {
    try {
      i18n.changeLanguage(lng);
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem("language", lng);
      }
      if (typeof window !== 'undefined' && window.location) {
        window.location.reload();
      }
    } catch (error) {
      console.error('Erro ao trocar idioma:', error);
    }
  };

  const LANGUAGE_OPTIONS = [
    { code: "pt-BR", label: "Português", emoji: "🇧🇷" },
    { code: "en", label: "English", emoji: "🇺🇸" },
    { code: "es", label: "Español", emoji: "🇪🇸" }
  ];

  const filteredLanguageOptions = LANGUAGE_OPTIONS.filter((lang) =>
    Array.isArray(enabledLanguages) && enabledLanguages.includes(lang.code)
  );

    // 🎂 FUNÇÃO PARA OBTER INICIAIS DO NOME
  const getInitials = (name) => {
    if (!name) return '';
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (loading || updateInProgress) {
    return <BackdropLoading />;
  }


  return (
    <LayoutProvider>
      <div className={clsx(classes.root, "logged-in-layout")}>

      {/* 🎂 MODAL DE ANIVERSÁRIO */}
      <BirthdayModal
        open={birthdayModalOpen}
        onClose={() => setBirthdayModalOpen(false)}
        user={user}
      />


      <AppBar
        position="fixed"
        className={classes.appBar}
        color="primary"
      >
        <Toolbar
          variant="dense"
          className={classes.toolbar}
        >
          {/* Logo da empresa */}
          <img
            className={classes.logo}
            style={{
              height: "40px",
              width: "auto",
              marginRight: "16px"
            }}
            alt="logo"
          />

          <div style={{ flexGrow: 1, display: "flex", justifyContent: "center" }}>
            <AppBarNavigationMenu />
          </div>

          <IconButton edge="start" onClick={colorMode.toggleColorMode}>
            {theme.mode === "dark" ? (
              <Brightness7Icon style={{ color: "white" }} />
            ) : (
              <Brightness4Icon style={{ color: "white" }} />
            )}
          </IconButton>


          <IconButton
            onClick={handleRefreshPage}
            aria-label={i18n.t("mainDrawer.appBar.refresh")}
            color="inherit"
          >
            <CachedIcon style={{ color: "white" }} />
          </IconButton>

          {user.id && <NotificationsPopOver volume={volume} />}

          <AnnouncementsPopover />

          {permissions.useInternalChat && <ChatPopover />}

          <div className="user-menu-wrapper">
            <StyledBadge
              overlap="circular"
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              variant="dot"
              onClick={handleMenu}
            >
              <Avatar
                alt={user.name}
                className={classes.avatar2}
                src={profileUrl}
              >
                {!profileUrl && getInitials(user.name)}
              </Avatar>
            </StyledBadge>

            <UserModal
              open={userModalOpen}
              onClose={() => setUserModalOpen(false)}
              onImageUpdate={(newProfileUrl) => setProfileUrl(newProfileUrl)}
              userId={user?.id}
            />

            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              getContentAnchorEl={null}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={menuOpen}
              onClose={handleCloseMenu}
              PaperProps={{
                style: {
                  minWidth: "150px",
                  maxWidth: "200px",
                  width: "auto",
                },
              }}
            >
              <MenuItem onClick={handleOpenUserModal}>
                {i18n.t("mainDrawer.appBar.user.profile")}
              </MenuItem>
              
              {/* Submenu de idiomas */}
              <MenuItem disabled style={{ fontWeight: "bold", opacity: 0.6 }}>
                <GlobeIcon style={{ marginRight: 8, width: 20, height: 20 }} />
                {i18n.t("mainDrawer.appBar.language")}
              </MenuItem>
              {filteredLanguageOptions.map((lang) => (
                <MenuItem 
                  key={lang.code}
                  onClick={() => handleLanguageMenuClick(lang.code)}
                  style={{ paddingLeft: 32 }}
                >
                  <span style={{ fontSize: '16px', marginRight: '8px' }}>
                    {lang.emoji}
                  </span>
                  {lang.label}
                </MenuItem>
              ))}
              
              {/* Controle de volume */}
              <MenuItem onClick={handleVolumeToggle}>
                {volume ? (
                  <VolumeUpIcon style={{ marginRight: 8, width: 20, height: 20 }} />
                ) : (
                  <VolumeOffIcon style={{ marginRight: 8, width: 20, height: 20 }} />
                )}
                {volume ? i18n.t("mainDrawer.appBar.volume.on") : i18n.t("mainDrawer.appBar.volume.off")}
              </MenuItem>

              {/* 📱 PWA Installation Options */}
              {!isInstalled && (isInstallable || isIOS) && (
                <>
                  <Divider />
                  <MenuItem onClick={isIOS ? handleShowIOSInstructions : handlePWAInstall}>
                    <GetAppIcon style={{ marginRight: 8, width: 20, height: 20 }} />
                    {isIOS ? i18n.t('pwa.installIOS') : i18n.t('pwa.install')}
                  </MenuItem>
                </>
              )}

              {/* Divider */}
              <Divider />


              <MenuItem onClick={handleClickLogout}>
                {i18n.t("mainDrawer.appBar.user.logout")}
              </MenuItem>
            </Menu>
          </div>
        </Toolbar>
      </AppBar>

      <main className={classes.content}>
        <div className={classes.appBarSpacer} />
        {children ? children : null}
      </main>

      {/* Modal de Informativos */}
      <Dialog
        open={showAnnouncementsModal}
        onClose={() => setShowAnnouncementsModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Informativos</DialogTitle>
        <DialogContent dividers>
          {selectedAnnouncement ? (
            <div>
              <Typography variant="h6" gutterBottom>
                {selectedAnnouncement.title}
              </Typography>
              <Typography variant="body1" style={{ whiteSpace: 'pre-line' }}>
                {selectedAnnouncement.text}
              </Typography>
              {selectedAnnouncement.mediaPath && (
                <div style={{ marginTop: 16 }}>
                  <img
                    src={`${backendUrl}/public/company${user.companyId}${selectedAnnouncement.mediaPath}`}
                    alt="Anexo"
                    style={{ maxWidth: '100%' }}
                  />
                </div>
              )}
              <Button
                onClick={() => setSelectedAnnouncement(null)}
                style={{ marginTop: 16 }}
                variant="outlined"
              >
                Voltar para lista
              </Button>
            </div>
          ) : (
            <List>
              {announcements.map((announcement) => (
                <ListItem
                  button
                  key={announcement.id}
                  onClick={() => setSelectedAnnouncement(announcement)}
                >
                  <ListItemAvatar>
                    <Avatar>
                      <NotificationsIcon style={{ width: 20, height: 20 }} />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={announcement.title}
                    secondary={
                      <>
                        <Typography
                          component="span"
                          variant="body2"
                          color="textPrimary"
                        >
                          Prioridade: {announcement.priority === 1 ? 'Alta' : announcement.priority === 2 ? 'Média' : 'Baixa'}
                        </Typography>
                        {` — ${new Date(announcement.createdAt).toLocaleDateString()}`}
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowAnnouncementsModal(false)}
            color="primary"
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNavigation user={user} onProfileClick={handleMenu} />

      <MobileFAB />

      <IOSInstallInstructionsDialog
        open={showIOSInstructions}
        onClose={() => setShowIOSInstructions(false)}
      />

      </div>
    </LayoutProvider>
  );
};

export default LoggedInLayout;